import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { ChevronRight } from 'lucide-react';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    SidebarGroup,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { type NavGroupItem, type NavItem } from '@/types';

function getCleanPath(url: string | undefined | null): string {
    if (!url || typeof url !== 'string') return '';
    return url.split('?')[0].split('#')[0];
}

function getCleanUrl(url: string | undefined | null): string {
    if (!url || typeof url !== 'string') return '';
    return url.split('#')[0];
}

function isItemActive(currentUrl: string, item: NavItem): boolean {
    const currentPath = getCleanPath(currentUrl);
    const currentFullUrl = getCleanUrl(currentUrl);
    const itemHref = getCleanUrl(item.href);
    const itemPath = getCleanPath(item.href);
    const hasQuery = itemHref.includes('?');

    return hasQuery
        ? currentFullUrl === itemHref
        : currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

function isGroupActive(currentUrl: string, group: NavGroupItem): boolean {
    if (group.href) {
        const currentPath = getCleanPath(currentUrl);
        const groupPath = getCleanPath(group.href);
        return currentPath === groupPath;
    }
    if (group.items) {
        return group.items.some((item) => isItemActive(currentUrl, item));
    }
    return false;
}

/** Rows shown in the collapsed flyout (matches expanded sidebar destinations). */
function getFlyoutNavRows(group: NavGroupItem): NavItem[] {
    const items = group.items?.length ? group.items : [];
    if (items.length > 0) {
        const rows: NavItem[] = [];
        if (group.href) {
            const groupHref = getCleanUrl(group.href);
            const duplicate = items.some((item) => getCleanUrl(item.href) === groupHref);
            if (!duplicate) {
                rows.push({
                    title: group.title,
                    href: group.href,
                    icon: group.icon,
                });
            }
        }
        rows.push(...items);
        return rows;
    }
    if (group.href) {
        return [
            {
                title: group.title,
                href: group.href,
                icon: group.icon,
            },
        ];
    }
    return [];
}

function NavGroupFlyout({
    group,
    flyoutRows,
    currentUrl,
}: {
    group: NavGroupItem;
    flyoutRows: NavItem[];
    currentUrl: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerRef = useRef<HTMLLIElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    const closeFlyout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setIsOpen(false);
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setIsOpen(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    }, []);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        closeFlyout();
    }, [currentUrl, closeFlyout]);

    useLayoutEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPos({ top: rect.top, left: rect.right + 4 });
        }
    }, [isOpen]);

    return (
        <SidebarMenuItem
            ref={triggerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <SidebarMenuButton
                size="sm"
                tooltip={{ children: group.title }}
            >
                <group.icon />
                <span>{group.title}</span>
            </SidebarMenuButton>
            {isOpen && pos && createPortal(
                <div
                    className="fixed z-60 min-w-40 rounded-md border border-sidebar-border bg-black p-1 text-white shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-left-2"
                    style={{ top: pos.top, left: pos.left }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="px-2 py-1.5 text-xs font-semibold text-white/70">
                        {group.title}
                    </div>
                    {flyoutRows.map((item) => (
                        <Link
                            key={item.href}
                            to={item.href}
                            onClick={closeFlyout}
                            className={cn(
                                'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-white hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground',
                                isItemActive(currentUrl, item) &&
                                    'bg-sidebar-primary font-medium text-sidebar-primary-foreground shadow-md',
                            )}
                        >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span>{item.title}</span>
                        </Link>
                    ))}
                </div>,
                document.body,
            )}
        </SidebarMenuItem>
    );
}

function NavGroupCollapsible({
    group,
    currentUrl,
}: {
    group: NavGroupItem;
    currentUrl: string;
}) {
    const groupActive = isGroupActive(currentUrl, group);
    const [open, setOpen] = useState(groupActive || !!group.defaultOpen);

    useEffect(() => {
        if (groupActive) setOpen(true);
    }, [groupActive]);

    return (
        <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton size="sm" tooltip={{ children: group.title }}>
                        <group.icon />
                        <span>{group.title}</span>
                        <ChevronRight
                            className={cn(
                                'ml-auto h-4 w-4 shrink-0 transition-transform duration-200',
                                open && 'rotate-90',
                            )}
                        />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {group.items!.map((item) => (
                            <SidebarMenuSubItem key={item.href}>
                                <SidebarMenuSubButton
                                    asChild
                                    size="sm"
                                    isActive={isItemActive(currentUrl, item)}
                                >
                                    <Link to={item.href}>
                                        <item.icon className="h-4 w-4 shrink-0" />
                                        <span>{item.title}</span>
                                    </Link>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

function NavGroupDirectLink({
    group,
    currentUrl,
}: {
    group: NavGroupItem;
    currentUrl: string;
}) {
    const currentPath = getCleanPath(currentUrl);
    const groupPath = getCleanPath(group.href);
    const isActive = currentPath === groupPath;

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                asChild
                size="sm"
                isActive={isActive}
                tooltip={{ children: group.title }}
            >
                <Link to={group.href!}>
                    <group.icon />
                    <span>{group.title}</span>
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}

export function NavGroupedMenu({ groups }: { groups: NavGroupItem[] }) {
    const { pathname, search } = useLocation();
    const { state } = useSidebar();
    const currentUrl = `${pathname}${search}`;
    const isCollapsed = state === 'collapsed';

    return (
        <SidebarGroup className="px-2 py-0">
            <SidebarMenu className="gap-0.5">
                {groups.map((group) => {
                    const hasSubItems = !!group.items?.length;
                    const isHrefOnly = !!group.href && !hasSubItems;
                    const flyoutRows = getFlyoutNavRows(group);

                    if (!isCollapsed) {
                        if (isHrefOnly) {
                            return (
                                <NavGroupDirectLink
                                    key={group.title}
                                    group={group}
                                    currentUrl={currentUrl}
                                />
                            );
                        }
                        if (hasSubItems) {
                            return (
                                <NavGroupCollapsible
                                    key={group.title}
                                    group={group}
                                    currentUrl={currentUrl}
                                />
                            );
                        }
                        return null;
                    }

                    if (flyoutRows.length > 0) {
                        return (
                            <NavGroupFlyout
                                key={group.title}
                                group={group}
                                flyoutRows={flyoutRows}
                                currentUrl={currentUrl}
                            />
                        );
                    }
                    return null;
                })}
            </SidebarMenu>
        </SidebarGroup>
    );
}

export default NavGroupedMenu;
